import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router';
import { ButtonValueProps, IconDropDownValueProps } from 'spindl-shared-components';

import { FILTER_DROPDOWN_OPTIONS } from '../../../lib/constants';
import { getTranslation } from '../../../lib/reactUtils';
import { addQueryParam, debounce, isFilterable, isSortable } from '../../../lib/utils';
import { TableStoreContext } from '../../../stores/TableStoreContext';
import { DatabasePageParams } from '../../pages/DatabasePage/types';
import { getDistinctFieldValuesUseCase } from './FieldColumnItem.interactor';
import { FieldColumnItemCombinedProps, FilterOption } from './types';
import { aggregateAllOptions, getSortIcon, processExistingFilters, processFilterOptions } from './utils';
import styles from './FieldColumnItem.module.scss';

const usePresenter = (props: FieldColumnItemCombinedProps): FieldColumnItemCombinedProps => {
  const { fieldType, text } = props;
  const fieldName = text?.value as string || '';
  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();
  const tableStore = useContext(TableStoreContext);
  const history = useHistory();

  const { filters, sortBy, sortOrder } = tableStore.tableConfigurationState?.[tableSlug || ''] || {};
  const fieldFilters = filters?.[fieldName];
  const isFieldSortable = isSortable(fieldType);
  const isFieldFilterable = isFilterable(fieldType);

  const [queryEnabled, setQueryEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>();
  const debouncedSetSearchQuery = debounce(setSearchQuery, 500);

  const { data: distinctFieldValues } = useQuery([
    'getDistinctFieldValues',
    databaseSlug,
    tableSlug,
    fieldName,
    searchQuery,
  ], async () => {
    if (tableSlug) {
      return getDistinctFieldValuesUseCase(
        databaseSlug,
        tableSlug,
        fieldName,
        { page: 1, perPage: FILTER_DROPDOWN_OPTIONS },
        searchQuery,
      );
    }
  }, { enabled: isFieldFilterable && queryEnabled });

  // Process queried and applied filter options
  // TODO: options on date fields should be formatted to remove the time portion
  const queriedFilterOptions = processFilterOptions(distinctFieldValues?.items, fieldFilters, !searchQuery);
  const appliedFilterOptions = processExistingFilters(fieldFilters);
  const allFilterOptions = aggregateAllOptions(queriedFilterOptions, appliedFilterOptions, !!searchQuery);

  const toggleFilter = (filterOption: FilterOption) => {
    tableStore.toggleFilter(tableSlug || '', fieldName, filterOption);
    const params = addQueryParam(history.location.search, { page: '1' }); // route to page 1
    history.push({ search: params });
  };

  const filterDropdown: IconDropDownValueProps | undefined = isFieldFilterable ? {
    ...props.filterDropdown,
    menuProps: {
      type: 'Search',
      preventDropdownClosing: true,
      onDropdownToggle: (show: boolean) => {
        setQueryEnabled(show);
      },
      dropDownItems: allFilterOptions?.map((filterOption) => ({
        className: styles.filterDropdownMenu,
        state: filterOption.selected ? 'Selected' : 'Unselected',
        type: 'DefaultTextIcon',
        text: {
          value: filterOption.displayValue,
        },
        onMouseDown: () => toggleFilter(filterOption),
      })),
      searchInput: {
        textValue: searchQuery,
        onTextChanged: (inputValue) => debouncedSetSearchQuery(inputValue),
      },
      bottomBarText: {
        value: getTranslation('select.items_count', {
          itemsDisplayed: allFilterOptions.length,
          totalItemsFound: distinctFieldValues?.totalItems,
        }),
      },
    },
    toggleProps: {
      className: styles.filterDropdown,
      type: 'IconButton',
      button: {
        icon: {
          asset: fieldFilters?.length ? 'FilterFilled' : 'FilterList',
        },
      },
    },
  } : undefined;

  const sortButton: ButtonValueProps | undefined = isFieldSortable ? {
    ...props.sortButton,
    icon: getSortIcon(fieldName, sortOrder, sortBy),
    onClick: () => tableStore.flipSorting(tableSlug || '', fieldName),
  } : undefined;

  return {
    ...props,
    filterDropdown,
    sortButton,
  };
};

export default usePresenter;
